/**
 * Cover image source: upload to media library, pick existing image, or paste URL.
 */
import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api';
import { MediaAsset } from '@/lib/types';

type SourceTab = 'upload' | 'library' | 'url';

interface CoverImagePickerProps {
  value: string;
  onChange: (url: string) => void;
}

function unwrapAssets(data: unknown): MediaAsset[] {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && Array.isArray((data as { results?: MediaAsset[] }).results)) {
    return (data as { results: MediaAsset[] }).results;
  }
  return [];
}

export default function CoverImagePicker({ value, onChange }: CoverImagePickerProps) {
  const [tab, setTab] = useState<SourceTab>('upload');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [library, setLibrary] = useState<MediaAsset[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (tab !== 'library') return;
    let cancelled = false;
    (async () => {
      setLibraryLoading(true);
      const res = await apiClient.getMediaAssets(null, 'image');
      if (!cancelled) {
        if (res.success) {
          setLibrary(unwrapAssets(res.data).filter(a => a.asset_type === 'image' && a.url));
        } else {
          setError(res.error || 'Failed to load media library');
        }
        setLibraryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tab]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('folder', 'Course Covers');
    const res = await apiClient.createMediaAsset(fd);
    setUploading(false);
    if (res.success && res.data?.url) {
      onChange(res.data.url);
    } else {
      setError(res.error || 'Upload failed');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const tabs: { id: SourceTab; label: string }[] = [
    { id: 'upload', label: 'Upload' },
    { id: 'library', label: 'Library' },
    { id: 'url', label: 'URL' },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="block text-sm font-medium text-slate-700">Cover image</label>
        {value && (
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-xs font-medium text-slate-500 hover:text-red-600 underline"
          >
            Clear
          </button>
        )}
      </div>

      {value && (
        <img
          src={value}
          alt="Cover preview"
          className="h-32 w-auto max-w-full rounded-lg object-cover border border-slate-200"
        />
      )}

      <div className="flex gap-1 border-b border-slate-200">
        {tabs.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => { setTab(t.id); setError(null); }}
            className={`px-3 py-1.5 text-xs font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <p className="text-xs text-red-600">{error}</p>
      )}

      {tab === 'upload' && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            className="rounded-lg border border-dashed border-slate-300 px-4 py-6 w-full text-sm text-slate-600 hover:border-blue-400 hover:text-blue-600 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : 'Choose an image to upload'}
          </button>
          <p className="mt-1 text-[11px] text-slate-400">Saved to Media Library (Course Covers).</p>
        </div>
      )}

      {tab === 'library' && (
        <div>
          {libraryLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
            </div>
          ) : library.length === 0 ? (
            <p className="text-sm text-slate-400 py-4">No images in your library yet. Upload one or paste a URL.</p>
          ) : (
            <div className="grid grid-cols-4 gap-2 max-h-56 overflow-y-auto">
              {library.map(asset => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => onChange(asset.url)}
                  title={asset.original_filename}
                  className={`rounded-lg overflow-hidden border-2 aspect-square ${
                    value === asset.url ? 'border-blue-600 ring-1 ring-blue-600' : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <img src={asset.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'url' && (
        <input
          type="url"
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="https://example.com/cover.jpg"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      )}
    </div>
  );
}
